<template>
  <div class="cdx-layout">
    <!-- 侧边栏 -->
    <aside class="cdx-sidebar">
      <div class="brand">
        <span class="brand-mark">众</span>
        <span class="brand-text">众盈物业</span>
      </div>
      <div class="menu-scroll">
        <el-menu
          :default-active="activeMenu"
          class="cdx-menu"
          router
        >
        <template v-for="group in visibleMenuGroups">
          <div :key="'g-' + group.title" class="menu-group-title">{{ group.title }}</div>
          <el-menu-item
            v-for="item in group.items"
            :key="item.key"
            :index="item.path"
          >
            <i :class="item.icon"></i><span>{{ item.title }}</span>
          </el-menu-item>
        </template>
      </el-menu>
      </div>
    </aside>

    <!-- 主区域 -->
    <div class="cdx-main">
      <header class="cdx-header">
        <div class="header-left">
          <span class="page-crumb">{{ currentTitle }}</span>
        </div>
        <div class="header-right">
          <el-button type="text" class="header-ai" icon="el-icon-chat-dot-round" @click="openAi">众盈客服</el-button>
          <el-button type="text" icon="el-icon-s-opportunity" @click="openRemote">远程支持</el-button>
          <el-button type="text" icon="el-icon-guide" @click="startIntro">功能引导</el-button>
          <span class="switch-label">当前项目</span>
          <project-switcher />
          <span class="switch-label">角色</span>
          <role-switcher />
          <el-badge :value="unreadCount" :hidden="unreadCount===0" class="notice-badge">
            <i class="el-icon-bell" style="font-size:18px;cursor:pointer" @click="$router.push('/system/notice')"></i>
          </el-badge>
          <div class="user-chip">
            <span class="avatar">A</span>
            <span class="uname">{{ userName }}</span>
            <span v-if="currentRoleName" class="role-tag">{{ currentRoleName }}</span>
          </div>
        </div>
      </header>
      <main class="cdx-content">
        <router-view :key="$route.path + '-' + routeKey" />
      </main>
      <intro-tour />
      <el-dialog title="远程技术支持" :visible.sync="remoteVisible" width="480px">
        <p class="remote-tip">高级技术支持通道，可反馈系统问题或申请厂商远程协助。</p>
        <el-form :model="remoteForm" label-width="90px">
          <el-form-item label="问题类型">
            <el-select v-model="remoteForm.type" style="width:100%">
              <el-option label="系统故障" value="BUG" />
              <el-option label="操作咨询" value="HELP" />
              <el-option label="远程协助" value="REMOTE" />
              <el-option label="其他" value="OTHER" />
            </el-select>
          </el-form-item>
          <el-form-item label="问题描述">
            <el-input v-model="remoteForm.content" type="textarea" :rows="4" placeholder="请描述现象、复现步骤" />
          </el-form-item>
          <el-form-item label="联系电话">
            <el-input v-model="remoteForm.phone" maxlength="11" />
          </el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="remoteVisible=false">取消</el-button>
          <el-button type="primary" @click="submitRemote">提交工单</el-button>
        </template>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import ProjectSwitcher from '@/components/ProjectSwitcher.vue';
import RoleSwitcher from '@/components/RoleSwitcher.vue';
import IntroTour from '@/components/IntroTour.vue';
import { noticeApi } from '@/api/system';
import { remoteSupportApi } from '@/api/modules';
import { PC_MENU_GROUPS } from '@/config/pcMenus';

export default {
  name: 'BasicLayout',
  components: { ProjectSwitcher, RoleSwitcher, IntroTour },
  data() {
    return {
      routeKey: 0,
      unreadCount: 0,
      remoteVisible: false,
      remoteForm: { type: 'HELP', content: '', phone: '' }
    };
  },
  computed: {
    ...mapState(['userName', 'currentRoleName', 'menuKeys', 'currentRoleCode']),
    activeMenu() {
      return this.$route.path;
    },
    currentTitle() {
      const meta = this.$route.meta || {};
      const title = meta.title || '';
      if (!title) return '';
      if (meta.parentTitle) return meta.parentTitle + ' > ' + title;
      const path = this.$route.path;
      for (const group of PC_MENU_GROUPS) {
        if (group.items.some(item => item.path === path)) {
          return group.title + ' > ' + title;
        }
      }
      return title;
    },
    visibleMenuGroups() {
      if (!this.menuKeys || this.currentRoleCode === 'SUPER_ADMIN') {
        return PC_MENU_GROUPS;
      }
      const allowed = new Set(this.menuKeys);
      return PC_MENU_GROUPS
        .map(group => ({
          ...group,
          items: group.items.filter(item => allowed.has(item.key))
        }))
        .filter(group => group.items.length > 0);
    }
  },
  created() {
    this.$root.$on('project-changed', () => {
      this.routeKey += 1;
    });
    this.$root.$on('role-changed', () => {
      this.routeKey += 1;
      this.redirectIfNoAccess();
    });
    this.fetchUnread();
    this._timer = setInterval(() => this.fetchUnread(), 60000);
  },
  beforeDestroy() {
    if (this._timer) clearInterval(this._timer);
    this.$root.$off('project-changed');
    this.$root.$off('role-changed');
  },
  methods: {
    redirectIfNoAccess() {
      if (!this.menuKeys || this.currentRoleCode === 'SUPER_ADMIN') return;
      const allowed = new Set(this.menuKeys);
      const path = this.$route.path.replace(/^\//, '');
      const permKey = this.$route.meta && this.$route.meta.permKey ? this.$route.meta.permKey : path;
      if (permKey && !allowed.has(permKey) && permKey !== 'home') {
        this.$router.replace('/home');
      }
    },
    async fetchUnread() {
      try { this.unreadCount = await noticeApi.unreadCount(); } catch(e) { /* ignore */ }
    },
    openAi() {
      this.$router.push('/ai/center').catch(() => {
        this.$message.info('智能客服入口暂不可用');
      });
    },
    openRemote() {
      this.remoteForm = { type: 'HELP', content: '', phone: '' };
      this.remoteVisible = true;
    },
    startIntro() {
      // 在扩展等业务页优先播本页引导；其它页播全局
      this.$root.$emit('start-intro', 'module');
    },
    async submitRemote() {
      if (!this.remoteForm.content) {
        this.$message.warning('请填写问题描述');
        return;
      }
      await remoteSupportApi.create({
        ticketType: this.remoteForm.type,
        content: this.remoteForm.content,
        contactPhone: this.remoteForm.phone
      });
      this.$message.success('已提交远程支持工单');
      this.remoteVisible = false;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.cdx-layout {
  display: flex;
  height: 100%;
}

// ---- 侧边栏：浅色、细线、极简 ----
.cdx-sidebar {
  width: $sidebar-width;
  background: $c-surface;
  border-right: 1px solid $c-border;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  height: 100vh;
  position: sticky;
  top: 0;

  .brand {
    height: $header-height;
    display: flex;
    align-items: center;
    gap: $space-2;
    padding: 0 $space-5;
    border-bottom: 1px solid $c-border;
    flex-shrink: 0;

    .brand-mark {
      width: 26px;
      height: 26px;
      border-radius: 7px;
      background: $c-accent;
      color: #fff;
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 15px;
    }
    .brand-text {
      font-weight: 600;
      font-size: 15px;
      color: $c-text;
    }
  }
}

.menu-scroll {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;

  // 美化滚动条
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: $c-border;
    border-radius: 2px;
    &:hover { background: $c-text-3; }
  }
}

.menu-group-title {
  font-size: 11px;
  letter-spacing: 1px;
  color: $c-text-3;
  padding: $space-4 $space-5 $space-2;
  text-transform: uppercase;
}

.cdx-menu {
  border-right: none;
  padding: 0 $space-3;
  padding-bottom: $space-5;

  ::v-deep .el-menu-item {
    height: 40px;
    line-height: 40px;
    border-radius: $radius-sm;
    margin-bottom: 2px;
    color: $c-text-2;
    font-size: 14px;

    i {
      color: $c-text-3;
      margin-right: $space-2;
    }
    &:hover {
      background: $c-surface-2;
      color: $c-text;
    }
    &.is-active {
      background: $c-accent-soft;
      color: $c-accent;
      font-weight: 600;
      i { color: $c-accent; }
    }
  }
}

// ---- 主区域 ----
.cdx-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.cdx-header {
  height: $header-height;
  background: $c-surface;
  border-bottom: 1px solid $c-border;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 $space-5;

  .page-crumb {
    font-size: 15px;
    font-weight: 600;
    color: $c-text;
  }
  .header-right {
    display: flex;
    align-items: center;
    gap: 8px;
    .el-button--text { color: $c-text-2; }
    .header-ai { color: $c-accent !important; font-weight: 600; }
  }
  .switch-label {
    font-size: 12px;
    color: $c-text-3;
  }
  .user-chip {
    display: flex;
    align-items: center;
    gap: $space-2;
    padding-left: $space-3;
    border-left: 1px solid $c-border;
    .avatar {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background: $c-text;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 13px;
    }
    .uname {
      font-size: 13px;
      color: $c-text-2;
    }
    .role-tag {
      font-size: 11px;
      color: $c-accent;
      background: $c-accent-soft;
      padding: 1px 6px;
      border-radius: 4px;
    }
  }
}

.cdx-content {
  flex: 1;
  overflow: auto;
  background: $c-bg;

  // 精细滚动条
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 3px;
    &:hover { background: #bbb; }
  }
}
.remote-tip { margin: 0 0 12px; color: #606266; line-height: 1.6; font-size: 13px; }
</style>
